/** @jsx jsx */
import React, { Fragment, useCallback, useMemo } from 'react';
import { css, jsx } from '@compiled/react';
import { formatInTimeZone } from 'date-fns-tz';
import isToday from 'date-fns/isToday';
import isYesterday from 'date-fns/isYesterday';
import Heading from '@atlaskit/heading';
import { Text } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
// TODO HL-1430 move these utils into our package once JWM business page is replaced with ours
import { useScrollToEnd } from '@atlassian/jira-business-scroll-to-end/src/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useSectionAnalytics } from '../../../common/ui/analytics/index.tsx';
import { ChartWithHeaderContainer } from '../../../common/ui/chart-with-header-container/index.tsx';
import { EmptyState } from '../../../common/ui/empty-state/index.tsx';
import { ScrollingLoader } from '../../../common/ui/scrolling-loader/index.tsx';
import type { PaginatedDataProps } from '../../avp-bridge/with-paginated-data/index.tsx';
import { BoundaryIds, SectionIds } from '../../../common/types.tsx';
import { ActivityItem } from './activity-item/index.tsx';
import messages from './messages.tsx';
import type { ActivityFeedItem } from './types.tsx';

type GroupedActivities = {
	key: string;
	label: string;
	items: ActivityFeedItem[];
}[];

const emptyIcon = (
	<svg width="96" height="96" viewBox="0 0 96 96" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path d="M79.8599 43.4902H31.1499V67.2902H79.8599V43.4902Z" fill="#1868DB" />
		<path d="M64.72 28.71H16.01V52.51H64.72V28.71Z" fill="#DDDEE1" />
		<path d="M64.7199 43.4902H31.1499V52.5102H64.7199V43.4902Z" fill="#09326C" />
		<path
			fill-rule="evenodd"
			clip-rule="evenodd"
			d="M76.8299 52.09L66.3399 62.58L60.9199 57.01L63.2599 54.74L66.3799 57.94L74.5299 49.79L76.8399 52.1L76.8299 52.09Z"
			fill="white"
		/>
	</svg>
);

export const RecentActivity = ({
	data,
	loadingNext,
	hasNextPage,
	fetchNext,
}: PaginatedDataProps<ActivityFeedItem>) => {
	const { formatDate, formatMessage } = useIntl();
	const { fireSectionAnalytics } = useSectionAnalytics(
		SectionIds.RecentActivity,
		BoundaryIds.RecentActivity,
	);

	const { onScroll } = useScrollToEnd({
		onScrollToEnd: fetchNext,
		isDisabled: loadingNext || !hasNextPage,
		options: {
			onEndReachedThreshold: 150,
		},
	});

	const itemsByDate: GroupedActivities | null = useMemo(
		() =>
			data.reduce<Array<{ key: string; label: string; items: ActivityFeedItem[] }>>(
				(groups, item) => {
					const publishedDate = new Date(item.published);
					const key = formatInTimeZone(publishedDate, 'UTC', 'yyyy-MM-dd');
					const lastGroup = groups[groups.length - 1];

					if (lastGroup?.key === key) {
						lastGroup.items.push(item);
					} else {
						let label;
						if (isToday(publishedDate)) {
							label = formatMessage(messages.todayLabel);
						} else if (isYesterday(publishedDate)) {
							label = formatMessage(messages.yesterdayLabel);
						} else {
							label = formatDate(key, {
								weekday: 'long',
								year: 'numeric',
								month: 'long',
								day: 'numeric',
							});
						}

						groups.push({ key, label, items: [item] });
					}

					return groups;
				},
				[],
			),
		[data, formatDate, formatMessage],
	);

	const onActivityItemClick = useCallback(
		(target: 'user' | 'issue') => {
			fireSectionAnalytics(target, { boundaryId: BoundaryIds.RecentActivity });
		},
		[fireSectionAnalytics],
	);

	if (!itemsByDate?.length) {
		return (
			<EmptyState
				icon={emptyIcon}
				headingText={formatMessage(messages.title)}
				descriptionText={formatMessage(messages.description)}
			/>
		);
	}

	return (
		<ChartWithHeaderContainer
			header={
				<>
					<Heading size="small" as="h2">
						{formatMessage(messages.chartHeading)}
					</Heading>
					<Text>{formatMessage(messages.chartDescription)}</Text>
				</>
			}
			chart={
				<div onScroll={onScroll} css={containerStyles}>
					{itemsByDate.map(({ key, label, items }) => (
						<Fragment key={key}>
							<h6 css={headerStyles}>{label}</h6>
							<ul css={listStyles}>
								{items.map((item) => (
									<ActivityItem key={item.id} item={item} onClick={onActivityItemClick} />
								))}
							</ul>
						</Fragment>
					))}
					{loadingNext && <ScrollingLoader />}
				</div>
			}
		/>
	);
};

const containerStyles = css({
	overflowY: 'auto',
	flexGrow: '1',
	marginRight: token('space.negative.150', '-12px'),
	paddingRight: token('space.150', '12px'),
});

const headerStyles = css({
	textTransform: 'none',
});

const listStyles = css({
	listStyleType: 'none',
	padding: 0,
	marginTop: 0,
	marginBottom: token('space.100', '8px'),
});
